<!-- @component Narration Control for the <AudioText/> component. -->
<script lang="ts">
	// IMPORTS
	import { assets } from '$app/paths';
	import { onMount } from 'svelte';
	import { createEventDispatcher } from 'svelte';
	import { toggleMuted } from '$stores/mutedStore';

	// PROPS
	export let id: string;
	export let active = false;
	export let muted: boolean;
	export let ended = false;

	// VARIABLES
	const src = `${assets}/audio/${id}.mp3`;
	let time = 0;
	let duration = 0;
	let paused = true;
	let audioEl: HTMLAudioElement;
	let progressRadius = 46;
	let offset = 0;

	const dispatch = createEventDispatcher();

	// LOGIC
	function togglePlay() {
		if (paused) {
			audioEl.play();
			ended = false;
		} else {
			audioEl.pause();
		}
	}

	function handleButtonClick() {
		if (muted) {
			toggleMuted();
		} else {
			togglePlay();
		}
	}

	// LIFECYCLE
	onMount(() => {
		duration = audioEl.duration;
	});

	// REACTIVITY
	$: {
		const circumference = progressRadius * 2 * Math.PI;

		if (time > 0) {
			offset = (time / duration) * circumference;
		}

		dispatch('audioInfo', { time, audioEl, ended });
	}
</script>

<button
	on:click="{handleButtonClick}"
	class="narration-control-container"
	class:active
	class:muted
>
	{#if !muted}
		<svg
			class="progress-container"
			viewBox="0 0 100 100"
			style="transform: scale(1, -1);"
		>
			<!-- Track circle -->
			<circle cx="50" cy="50" r="{progressRadius}" class="progress-track"
			></circle>

			<!-- Progress circle -->
			<circle
				cx="50"
				cy="50"
				r="{progressRadius}"
				class="progress-bar"
				style="stroke-dasharray: {2 *
					Math.PI *
					46}; stroke-dashoffset: {offset};"
			></circle>
		</svg>
	{/if}
	{#if muted}
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
			class="icon lucide lucide-volume-x"
			><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line
				x1="22"
				x2="16"
				y1="9"
				y2="15"
			></line><line x1="16" x2="22" y1="9" y2="15"></line></svg
		>
	{:else if paused}
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="28"
			height="28"
			viewBox="-2 0 24 24"
			stroke-linecap="round"
			stroke-linejoin="round"
			class="icon lucide lucide-play"
			><polygon points="5 3 19 12 5 21 5 3"></polygon></svg
		>
	{:else}<svg
			xmlns="http://www.w3.org/2000/svg"
			width="28"
			height="28"
			viewBox="0 0 24 24"
			fill="none"
			stroke-linecap="round"
			stroke-linejoin="round"
			class="icon lucide lucide-pause"
			><rect width="4" height="16" x="6" y="4"></rect><rect
				width="4"
				height="16"
				x="14"
				y="4"
			></rect></svg
		>{/if}

	<audio
		{src}
		bind:this="{audioEl}"
		bind:currentTime="{time}"
		bind:duration
		bind:paused
		preload="auto"
		{muted}
		on:ended="{() => {
			ended = true;
			time = 0;
		}}"
	></audio>
	<span class="formatted-time"
		>Time: {time} / Duration: {duration} / Offset: {offset}</span
	>
</button>

<style lang="scss">
	@use '$styles/mq' as *;
	@use '$styles/colors' as *;

	.narration-control-container {
		display: flex;
		position: fixed;
		right: -60px;
		bottom: 20px;
		justify-content: center;
		align-items: center;
		transform-origin: 0% 0%;
		z-index: 999;
		transition: all 0.5s ease-in-out;
		border-radius: 50%;
		background-color: var(--theme-color-background);
		width: 60px;
		height: 60px;

		&.muted {
			width: 45px;
			height: 45px;
		}

		.formatted-time {
			position: absolute;
			bottom: -10px;
			left: 50%;
			transform: translate(-50%, -50%);
			opacity: 0;
			color: var(--theme-accent);
			font-weight: 300;
			font-size: 0.5rem;
		}

		svg.icon {
			fill: var(--theme-accent);
			stroke: var(--theme-accent);
			display: block;
		}

		&:hover {
			svg.icon {
				fill: #bf6569;
				stroke: #bf6569;
			}
		}

		&.active {
			right: 20px;
			bottom: 20px;
		}
	}

	.progress-container {
		position: absolute;
		width: 100%;
		height: 100%;
	}

	.progress-track,
	.progress-bar {
		fill: none;
		stroke-width: 5;
	}

	.progress-track {
		stroke: var(--theme-accent); // or any light color based on your theme
	}

	.progress-bar {
		stroke: $rfe-gray-2;
		transform: rotate(90deg);
		transform-origin: 50% 50%;
	}
</style>
